import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, distinctUntilChanged, map, take, tap } from 'rxjs';

import { isEqual } from 'lodash';
import { filterNil } from '@ngneat/elf';
import { UpgradePlanEvaluationType } from 'src/app/services/paywall-indicator.service';
import { FeatureManagementService } from 'src/app/services/feature-management.service';
import { Collaboration, AccessControl } from '@pncl/common-models';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { ObserverModeService } from 'src/app/services/observer-mode.service';
import { UserService } from '../../../services/user.service';
import { InstitutionPermissionState, RecordingMode, User } from '../../../models/user';
import { SettingsService } from '../../../services/settings.service';
import { Visibility } from '../../../models/session';
import { SettingsNotificationService } from '../../../settings/shared/services/settings-notifications.service';
import { CapabilityTierService } from '../../../services/capability-tier.service';
import { SessionRecordingAccess } from '../../../models/analytics';
import { RecordingAccess } from '../../../models/space-recording';
import {
  RecordingAccessOption,
  SpaceRecordingService,
} from '../../../services/space-recording.service';
import { OBSERVER_MODE_LEARN_MORE_URL } from '../../../services/observer-mode.utils';
import { UserSettingsWarningTheme } from './user-settings-warning.component';
import { UserSettingsPermissionsSelections } from './user-settings-permissions-selections';
// Define Capability from AccessControl to avoid TS errors
import Capability = AccessControl.Capability;

@UntilDestroy()
@Component({
  selector: 'app-user-settings-permissions[form]',
  templateUrl: './user-settings-permissions.component.html',
  styleUrls: ['./user-settings-permissions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsPermissionsComponent implements OnInit {
  private user$ = this.userService.user.pipe(
    map((x) => x?.user),
    filterNil(),
  );

  @Input() isMobile = false;
  @Input() PANELS: any;
  @Input() form!: UntypedFormGroup;
  @Output() goBack = new EventEmitter<any>();
  @Output() formChangedEvent: EventEmitter<boolean> = new EventEmitter();

  settingsForm?: UntypedFormGroup;
  user?: User;
  isLoading = false;
  public readonly RecordingMode = RecordingMode;
  public readonly InstitutionPermissionState = InstitutionPermissionState;
  public readonly defaultSpaceVisibilityPublicDisabledTooltip = this.translateService.instant(
    'This setting isn’t available when Default Space access is set to Public.',
  );
  public isDefaultSpaceVisibilityPublic = false;
  public initialFormValue: unknown;
  public formChanged$ = new BehaviorSubject<boolean>(false);
  public canUseAutomaticRecording$ =
    this.featureManagementService.isAutomaticRecordingCapabilityEnabled$;
  public isParticipantRecordingEnabled$ = this.user$.pipe(
    map(
      (user) =>
        user.institution.settings?.crAdvancedSettings?.egressType ===
        Collaboration.Spaces.EgressType.PARTICIPANT,
    ),
  );
  public participantRecordingDisabledTooltip = this.translateService.instant(
    'Recording options aren’t available when recording is configured to be per-participant',
  );
  public canUsePrivacyAwareCloudRecording$ = this.capabilityTierService.shouldEnableCapability$(
    Capability.PRIVACY_AWARE_CLOUD_RECORDING,
  );
  public readonly UpgradePlanEvaluationType = UpgradePlanEvaluationType;
  public readonly RecordingAccess = RecordingAccess;
  public readonly UserSettingsWarningTheme = UserSettingsWarningTheme;
  public readonly observerModeLearnMoreUrl = OBSERVER_MODE_LEARN_MORE_URL;

  // Recording access options for dropdown
  public recordingAccessOptions$ = new BehaviorSubject<RecordingAccessOption[]>([]);

  protected readonly IconTypes = IconTypes;
  public readonly Visibility = Visibility;

  readonly waitingRoomOptions = {
    setting: 'enableWaitingRoom',
    selections: new UserSettingsPermissionsSelections(this.translateService)
      .setDefaultSelections()
      .setDescriptions([
        this.translateService.instant(
          'New Spaces will have a Waiting Room by default. Hosts can turn it off in settings. Existing Spaces remain unchanged',
        ),
        this.translateService.instant(
          "New Spaces won't have a Waiting Room by default. Hosts can turn it on in settings. Existing Spaces remain unchanged",
        ),
        this.translateService.instant(
          'No Spaces will have a Waiting Room. Hosts cannot turn it on. Guests will need to sign-in to open a Space',
        ),
        this.translateService.instant(
          'Every Space will have a Waiting Room. Hosts cannot turn it off',
        ),
      ])
      .build(),
  };

  readonly messageOtherParticipantsOptions = {
    setting: 'messageOtherParticipants',
    selections: new UserSettingsPermissionsSelections(this.translateService)
      .setDefaultSelections()
      .setDescriptions([
        this.translateService.instant(
          'Participants can message other participants by default. Hosts can turn this off in settings. Existing Spaces remain unchanged.',
        ),
        this.translateService.instant(
          'Participants cannot message other participants by default. Hosts can turn this on in settings. Existing Spaces remain unchanged.',
        ),
        this.translateService.instant(
          'For all Spaces, participants cannot message other participants. Hosts cannot change this setting in a Space.',
        ),
        this.translateService.instant(
          'For all Spaces, participants can message other participants. Hosts cannot change this setting in a Space.',
        ),
      ])
      .build(),
  };

  readonly downloadSpacesOrBoardsOptions = {
    setting: 'downloadSpacesOrBoards',
    selections: new UserSettingsPermissionsSelections(this.translateService)
      .setSelections([
        {
          value: InstitutionPermissionState.ALWAYS_ON,
          title: this.translateService.instant('Always On (Hosts Cannot Modify)'),
        },
        {
          value: InstitutionPermissionState.ALWAYS_OFF,
          title: this.translateService.instant('Always Off (Hosts Cannot Modify)'),
        },
      ])
      .setDescriptions([
        this.translateService.instant(
          'Hosts can download content from Spaces or boards to their device',
        ),
        this.translateService.instant('Hosts cannot download any content from Spaces'),
      ])
      .build(),
  };

  readonly leaderModeOptions = {
    setting: 'leaderMode',
    selections: new UserSettingsPermissionsSelections(this.translateService)
      .setSelections([
        {
          value: InstitutionPermissionState.DEFAULT_ON,
          title: this.translateService.instant('Default On'),
        },
        {
          value: InstitutionPermissionState.DEFAULT_OFF,
          title: this.translateService.instant('Default Off'),
        },
      ])
      .setDescriptions([
        this.translateService.instant(
          'Leader mode is enabled automatically when the first host enters.',
        ),
        this.translateService.instant('Leader mode has to be enabled manually.'),
      ])
      .build(),
  };

  readonly childModeOptions = {
    setting: 'childMode',
    selections: new UserSettingsPermissionsSelections(this.translateService)
      .setSelections([
        {
          value: InstitutionPermissionState.DEFAULT_ON,
          title: this.translateService.instant('Default On'),
        },
        {
          value: InstitutionPermissionState.DEFAULT_OFF,
          title: this.translateService.instant('Default Off'),
        },
      ])
      .setDescriptions([
        this.translateService.instant('Child mode is enabled automatically for all participants.'),
        this.translateService.instant('Child mode has to be enabled manually.'),
      ])
      .build(),
  };

  readonly isExportImportSpaceFileEnabled =
    this.featureManagementService.isExportImportSpaceFileEnabled();
  readonly isNewSessionsFlowEnabled = this.flagsService.isFlagEnabled(
    FLAGS.ENABLE_NEW_SESSIONS_FLOW,
  );
  readonly isRecordWbOnCloudEnabled = this.flagsService.isFlagEnabled(FLAGS.RECORD_WB_ON_CLOUD);
  readonly isObserverModeEnabled$ = this.observerModeService.observerModeEnabled$;

  constructor(
    private userService: UserService,
    private settingsService: SettingsService,
    private translateService: TranslateService,
    private settingsNotificationService: SettingsNotificationService,
    protected featureManagementService: FeatureManagementService,
    private capabilityTierService: CapabilityTierService,
    private flagsService: FlagsService,
    public readonly spaceRecordingService: SpaceRecordingService,
    private observerModeService: ObserverModeService,
  ) {}

  ngOnInit(): void {
    this.settingsForm = this.form?.controls.settings as UntypedFormGroup;

    const settingsGroup = this.form.get('settings');
    const defaultSpaceVisibilityControl = settingsGroup?.get('defaultSpaceVisibility');
    const preventMakingSpacesPublicControl = settingsGroup?.get('preventMakingSpacesPublic');
    const preventAddingExternalUsersToSpacesControl = settingsGroup?.get(
      'preventAddingExternalUsersToSpaces',
    );
    defaultSpaceVisibilityControl?.valueChanges
      .pipe(
        map((visibility) => visibility === Visibility.PUBLIC),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe((isPublic) => {
        this.isDefaultSpaceVisibilityPublic = isPublic;
        if (isPublic) {
          preventMakingSpacesPublicControl?.setValue(false, { emitEvent: false });
          preventAddingExternalUsersToSpacesControl?.setValue(false, { emitEvent: false });
        }
      });

    this.userService.user.pipe(untilDestroyed(this)).subscribe((res) => {
      this.user = res?.user;
      this.initForm();
    });
  }

  initForm(): void {
    this.form.patchValue({
      ...this.user?.institution,
      settings: {
        ...(this.user?.institution?.settings ?? {}),
        crDefaultAccess: this.maskDefaultRecordingAccess(
          this.user?.institution?.settings?.crDefaultAccess || RecordingAccess.HOSTS,
        ),
        defaultSpaceVisibility:
          this.user?.institution?.settings?.defaultSpaceVisibility ??
          this.user?.institution?.defaultSettings?.defaultSpaceVisibility,
      },
      permissions: {
        ...(this.user?.institution?.permissions ?? {}),
      },
    });

    this.initialFormValue = this.form.value;
    this.form.valueChanges
      .pipe(
        map(() => !isEqual(this.initialFormValue, this.form.value)),
        tap((x) => this.formChangedEvent.emit(x)),
        untilDestroyed(this),
      )
      .subscribe(this.formChanged$);

    // Watch for changes to crEnablePublicRecordings and automatically adjust crDefaultAccess
    const crEnablePublicRecordingsControl = this.form.get('settings.crEnablePublicRecordings');
    if (crEnablePublicRecordingsControl) {
      // Emit initial value
      this.updateRecordingAccessOptions(crEnablePublicRecordingsControl.value ?? false);

      // Subscribe to changes
      crEnablePublicRecordingsControl.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe((isPublicRecordingsEnabled) => {
          // Update recording access options based on toggle state
          this.updateRecordingAccessOptions(isPublicRecordingsEnabled);

          const crDefaultAccessControl = this.form.get('settings.crDefaultAccess');
          const currentDefaultAccess = crDefaultAccessControl?.value;

          // If public recordings are disabled and default access is PUBLIC,
          // automatically change it to HOSTS_AND_PARTICIPANTS
          if (
            !isPublicRecordingsEnabled &&
            currentDefaultAccess === RecordingAccess.PUBLIC &&
            crDefaultAccessControl
          ) {
            crDefaultAccessControl.setValue(RecordingAccess.HOSTS_AND_PARTICIPANTS, {
              emitEvent: true,
            });
          }
        });
    }

    const crForceAdminOnlyAccessControl = this.form.get('settings.crForceAdminOnlyAccess');
    const crDefaultAccessControl = this.form.get('settings.crDefaultAccess');
    if (crForceAdminOnlyAccessControl && crDefaultAccessControl) {
      crForceAdminOnlyAccessControl.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe((isAdminOnlyAccessForced) => {
          if (isAdminOnlyAccessForced) {
            crDefaultAccessControl.setValue(RecordingAccess.ADMINS, { emitEvent: true });
          }
        });
    }
  }

  get displayedDefaultRecordingAccess(): RecordingAccess {
    return this.maskDefaultRecordingAccess(
      this.form?.value?.settings?.crDefaultAccess || RecordingAccess.HOSTS,
    );
  }

  setDefaultRecordingAccess(access: RecordingAccess): void {
    const control = this.form.get('settings.crDefaultAccess');
    if (control) {
      control.setValue(access);
    }
  }

  private maskDefaultRecordingAccess(access: RecordingAccess): RecordingAccess {
    if (this.isNewSessionsFlowEnabled && access === RecordingAccess.PUBLIC) {
      return RecordingAccess.HOSTS_AND_PARTICIPANTS;
    }
    return access;
  }

  /**
   * Updates the recording access options based on whether public recordings are enabled.
   * Disables the PUBLIC option if public recordings are not enabled.
   * With the sessions flow, only SessionRecordingAccess options are shown.
   */
  private updateRecordingAccessOptions(isPublicRecordingsEnabled: boolean): void {
    const options = this.spaceRecordingService.getRecordingAccessOptions({
      includeSubtitles: true,
    });
    const sessionRecordingAccessValues = new Set<string>(Object.values(SessionRecordingAccess));
    const visibleOptions = this.isNewSessionsFlowEnabled
      ? options.filter((option) => sessionRecordingAccessValues.has(option.value))
      : options;
    this.recordingAccessOptions$.next(
      visibleOptions.map((option) => ({
        ...option,
        disabled:
          option.value === RecordingAccess.PUBLIC &&
          !isPublicRecordingsEnabled,
      })),
    );
  }

  submitChanges(): void {
    if (!this.user || this.isLoading) {
      return;
    }

    this.isLoading = true;
    const updatedUser = {
      ...this.user,
    };

    updatedUser.institution.settings = {
      ...this.user?.institution.settings,
      ...this.form.value.settings,
    };

    updatedUser.institution.permissions = {
      ...this.user?.institution.permissions,
      ...this.form.value.permissions,
    };

    this.userService
      .updateInstitution(updatedUser)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.refresh(updatedUser);
          if (this.isMobile) {
            this.goBack.emit();
          }
        },
        error: () => {
          this.settingsService.showToast(
            {
              title: this.translateService.instant('Update failed'),
              description: this.translateService.instant('An error occurred. Please try again.'),
            },
            true,
          );
        },
        complete: () => {
          this.isLoading = false;
          this.formChanged$.next(false);
          this.formChangedEvent.emit(false);
        },
      });
  }

  refresh(userPayload: User): void {
    const userFromService = this.userService.user.getValue();
    this.userService.user.next({
      ...userFromService,
      user: userPayload,
    });

    this.settingsNotificationService.showSuccessSettingsUpdateNotification();
  }

  onVisibilityChanged(visibility: Visibility): void {
    this.form.get('settings')?.get('defaultSpaceVisibility')?.setValue(visibility);
  }
}
